import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import * as Sentry from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';
import App from './App';
import { sanitizeSentryEvent } from './utils/sentryUtils';
import { queryClient } from './lib/queryClient';
import { I18nProvider } from './lib/i18n';
import { ConfigProvider } from './context/ConfigContext';
import { config } from './lib/config';

if (config.useSentry) {
  Sentry.init({
    dsn: 'https://4630e0850250f045d2ca6e8d1659879e@o4508160430833664.ingest.de.sentry.io/4508160433061968',
    environment: config.env,
    release: config.sentryRelease,
    debug: config.env === 'staging',

    // Allow more data collection
    sendDefaultPii: false,

    integrations: [
      Sentry.browserTracingIntegration({
        tracePropagationTargets: [
          /^https:\/\/qss7df48sc\.execute-api\.eu-north-1\.amazonaws\.com\/production\/api/,
          'localhost:4000',
          ...(config.env === 'development' ? ['localhost'] : []),
        ],
      }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.httpClientIntegration(),
      Sentry.breadcrumbsIntegration(),
      Sentry.globalHandlersIntegration({
        onerror: true,
        onunhandledrejection: true,
      }),
    ],

    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 1.0,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
      // Apply only message sanitization
      return sanitizeSentryEvent(event);
    },

    initialScope: {
      user: {}, // Empty user object
    },

    enableTracing: true,
    autoSessionTracking: true,
  });

  // Allow user data
  Sentry.setUser(null);
}

const configParam = new URLSearchParams(window.location.search).get('config');

const getInitialConfigId = (configId) => {
  if (configId && configId !== 'null') {
    return configId;
  }

  return '916fdbb5-db1e-4309-b256-296a6b554c4c'; // Let's use sekasin as default for now
};

const container = document.getElementById('root');
const root = ReactDOM.createRoot(container);

root.render(
  <Sentry.ErrorBoundary fallback={<p>An unknown error has occurred</p>} showDialog>
    <QueryClientProvider client={queryClient}>
      <ConfigProvider initialConfigId={getInitialConfigId(configParam)}>
        <I18nProvider>
          <App />
        </I18nProvider>
      </ConfigProvider>
    </QueryClientProvider>
  </Sentry.ErrorBoundary>
);
